import { Modal, Row } from 'antd';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { turnModalOff } from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import { getModalData, getModalOpen } from 'store/app/selectors';
import { resetPwaSheetDragState } from 'utils/usePwaBottomSheetDragClose';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import styled from 'styled-components';

const ModalWindowContainerWrapper = styled(Modal)`
  .ant-modal-content {
    border-radius: 20px !important;
    min-width: fit-content;
  }

  .ant-modal-title {
    text-align: center;
  }

  .ant-modal-header {
    margin-top: 25px;
  }

  &.extraction-rule-modal--pwa {
    top: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: env(safe-area-inset-top, 0px) 0 0 !important;
    box-sizing: border-box !important;
  }

  &.extraction-rule-modal--pwa .ant-modal-header {
    margin-top: 0;
    padding: 12px 16px 4px;
    flex-shrink: 0;
  }

  &.extraction-rule-modal--pwa .ant-modal-content {
    max-height: calc(100dvh - env(safe-area-inset-top, 0px));
    min-height: calc(100dvh - env(safe-area-inset-top, 0px));
    height: calc(100dvh - env(safe-area-inset-top, 0px));
    border-radius: 0 !important;
    display: flex;
    flex-direction: column;
  }

  &.extraction-rule-modal--pwa .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 4px 16px calc(8px + env(safe-area-inset-bottom, 0px));
  }

  &.extraction-rule-modal .ant-modal-header {
    margin-top: 12px;
    margin-bottom: 0;
    padding-bottom: 4px;
    flex-shrink: 0;
  }

  &.extraction-rule-modal .ant-modal-title {
    font-size: 16px;
    font-weight: 600;
  }

  &.extraction-rule-modal .ant-modal-content {
    max-height: calc(100vh - 48px);
    min-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
  }

  &.extraction-rule-modal .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 4px 20px 4px;
    display: flex;
    flex-direction: column;
  }

  /* Generic PWA phone: bottom sheet for form / warning modals */
  &.scope-pwa-modal {
    top: auto !important;
    bottom: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  &.scope-pwa-modal .ant-modal-content {
    min-width: 0 !important;
    max-width: 100% !important;
    border-radius: 20px 20px 0 0 !important;
    max-height: calc(100dvh - 8px - env(safe-area-inset-top, 0px));
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }

  &.scope-pwa-modal .ant-modal-header {
    margin-top: 12px;
  }
`;

function isFullHeightModal(
  data: { fullHeight?: boolean } | undefined
): boolean {
  return data?.fullHeight === true;
}

const PWA_SHEET_STYLE: React.CSSProperties = {
  maxWidth: '100%',
  width: '100%',
  minWidth: 0,
  top: 'auto',
  bottom: 0,
  margin: 0,
  paddingBottom: 0,
};

export const ModalWindowContainer: React.FC = () => {
  const dispatch = useDispatch();
  const modalData = useSelector(getModalData);
  const modalOpen = useSelector(getModalOpen);
  const [displayModalData, setDisplayModalData] = React.useState(modalData);

  React.useEffect(() => {
    if (modalOpen && modalData) {
      setDisplayModalData(modalData);
    }
  }, [modalOpen, modalData]);

  const activeModalData = modalOpen ? modalData : displayModalData;
  const fullHeightModal = isFullHeightModal(activeModalData);
  const pwaMobileNav = usePwaMobileNavEnabled();

  const closeModal = React.useCallback(() => {
    if (activeModalData?.onClose) {
      activeModalData.onClose();
    }
    resetPwaSheetDragState();
    dispatch(turnModalOff());
  }, [activeModalData, dispatch]);

  useOverlayBackHandler(
    Boolean(modalOpen),
    closeModal,
    `modal:${String(activeModalData?.type ?? 'modal')}`,
    pwaMobileNav
  );

  let modalStyles: Record<string, React.CSSProperties> = {};
  let style: React.CSSProperties = {};

  if (activeModalData?.type === WindowType.VIDEO) {
    modalStyles = { content: { backgroundColor: 'transparent', padding: 0 } };
    style = pwaMobileNav
      ? { maxWidth: '100vw', width: '100vw', minWidth: 0, top: 0, margin: 0 }
      : { minWidth: '80vw' };
  }
  if (activeModalData?.type === WindowType.POST_JOURNAL) {
    style = pwaMobileNav ? { ...PWA_SHEET_STYLE } : { minWidth: 900 };
  }
  if (activeModalData?.type === WindowType.EXCEL_TRANSACTIONS) {
    // Wide modal layout for Excel transactions flow
    style = pwaMobileNav
      ? { ...PWA_SHEET_STYLE }
      : {
          minWidth: 1200,
          top: 24,
        };
  }
  if (activeModalData?.type === WindowType.WARNING) {
    if (activeModalData?.title === 'Save Filter View') {
      // Reduce size by 40% for "Save Filter View" modal (800 * 0.6 = 480)
      style = pwaMobileNav ? { ...PWA_SHEET_STYLE } : { minWidth: 480 };
    } else if (fullHeightModal) {
      if (pwaMobileNav) {
        style = {
          maxWidth: '100%',
          width: '100%',
          top: 0,
          margin: 0,
          paddingBottom: 0,
        };
        modalStyles = {
          content: {
            maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px))',
            minHeight: 'calc(100dvh - env(safe-area-inset-top, 0px))',
            height: 'calc(100dvh - env(safe-area-inset-top, 0px))',
            borderRadius: 0,
          },
        };
      } else {
        style = {
          minWidth: 820,
          maxWidth: 'calc(100vw - 32px)',
          top: 16,
          paddingBottom: 0,
        };
        modalStyles = {
          content: {
            maxHeight: 'calc(100vh - 48px)',
            minHeight: 'calc(100vh - 48px)',
          },
        };
      }
    } else {
      style = pwaMobileNav ? { ...PWA_SHEET_STYLE } : { minWidth: 800 };
    }
  }

  // Any other modal types on PWA also bottom-sheet (FORM, ADD_MEMBER, etc.)
  if (
    pwaMobileNav &&
    !fullHeightModal &&
    activeModalData?.type !== WindowType.VIDEO &&
    Object.keys(style).length === 0
  ) {
    style = { ...PWA_SHEET_STYLE };
  }

  // Hide header for modals with custom headers (title is a single space)
  const shouldHideHeader =
    activeModalData?.type === WindowType.VIDEO ||
    activeModalData?.type === WindowType.POST_JOURNAL ||
    activeModalData?.type === WindowType.EXCEL_TRANSACTIONS;

  const usePwaSheetClass =
    pwaMobileNav && !fullHeightModal && activeModalData?.type !== WindowType.VIDEO;

  const extractionModalClassName = [
    fullHeightModal ? 'extraction-rule-modal' : undefined,
    fullHeightModal && pwaMobileNav ? 'extraction-rule-modal--pwa' : undefined,
    usePwaSheetClass ? 'scope-pwa-modal' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <ModalWindowContainerWrapper
      // Above DrawerContainer (1200) so modals opened from drawers (e.g. OSS upgrade) sit on top.
      zIndex={1300}
      className={extractionModalClassName || undefined}
      title={
        shouldHideHeader ? null : (
          <>
            {activeModalData?.icon && (
              <Row justify={'center'} style={{ marginBottom: 15 }}>
                {activeModalData?.icon}
              </Row>
            )}{' '}
            <Row justify={'center'}>{activeModalData?.title}</Row>
          </>
        )
      }
      centered={
        !usePwaSheetClass &&
        activeModalData?.type !== WindowType.EXCEL_TRANSACTIONS &&
        !fullHeightModal
      }
      open={modalOpen}
      destroyOnClose
      onCancel={closeModal}
      keyboard
      afterClose={() => {
        resetPwaSheetDragState();
        setDisplayModalData(undefined);
      }}
      footer={false}
      styles={modalStyles}
      style={style}
      maskClosable={activeModalData?.maskClosable ?? true}
      closable={
        activeModalData?.type === WindowType.VIDEO
          ? true
          : shouldHideHeader
          ? false
          : true
      }
      aria-label={
        activeModalData?.type === WindowType.VIDEO && activeModalData?.title
          ? String(activeModalData.title)
          : undefined
      }
    >
      {activeModalData?.component}
    </ModalWindowContainerWrapper>
  );
};
